import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, IRootState } from '../../redux/rootReducer';
import { useParams, useSearchParams } from 'react-router-dom';
import { formatPhoneNumberIntl } from 'react-phone-number-input';
import SwiperButton from '../SwiperButton/SwiperButton';
import { getVendorLocationVisit, getVendorVisit } from '../../redux/vendorVisit/actions/vendorVisit.action';
import { useGeolocation } from '../../hooks/geoLocation';
import { ToastType } from '../appointments/Appointments';
import Toast from '../toast/Toast';
import { openAmazonDoor, resetAmazonDoor } from '../../redux/appointment/actions/appointment.action';
import SorryPineapple from '../../images/pineapple-sorry.svg';
import Popup from '../common/popup/Popup';
import LockIcon from '../icons/LockIcon';
import LeaasingNumberIcon from '../icons/LeasingNumberIcon';
import Loader from '../common/loader/Loader';
import styles from './VendorAppintments.module.css';
import { AmazoneFlag } from '../../utils/constants/constants';

export interface vendorAppointmentAccessCodeProps {
    vendorId: string;
    latitude: string;
    longitude: string;
}

export interface amazonDoorProps {
    locationId: string;
    appointmentId?: string;
    vendorId?: string;
    latitude: number;
    longitude: number;
    timestamp: number;
    amazonFlag?: string;
}

const isAccessible = (
    vendorAppointmentStartDate: string,
    vendorAppointmentEndDate: string,
    vendorAppointmentStartTime: string,
    vendorAppointmentEndTime: string,
) => {
    // Use HH:mm for 24-hour format, hh:mm a for 12-hour format with AM/PM
    const startDate = moment(`${vendorAppointmentStartDate} ${vendorAppointmentStartTime}`, 'YYYY-MM-DD hh:mm a').toDate();

    const endDate = moment(`${vendorAppointmentEndDate} ${vendorAppointmentEndTime}`, 'YYYY-MM-DD hh:mm a').toDate();

    const currentDate = new Date();
    const startTimeRange = new Date(startDate.getTime() - 15 * 60000);
    const endTimeRange = new Date(endDate.getTime() + 15 * 60000);

    if (currentDate < startTimeRange) {
        return 0;
    } else if (currentDate >= startTimeRange && currentDate <= endTimeRange) {
        return 1;
    } else {
        return -1;
    }
};

const VendorAppointments = () => {
    const dispatch: AppDispatch = useDispatch();
    const { data: vendorData, loading: vendorLoading } = useSelector((state: IRootState) => state.vendorVisitReducer);
    const {
        loading: doorOpenloading,
        data: doorOpenData,
        error: doorOpenError,
    } = useSelector((state: IRootState) => state.appointmentReducer.amazonDoor);
    const [showToast, setShowToast] = useState(false);
    const [toastType, setToastType] = useState<ToastType>('error');
    const [toastMessage, setToastMessage] = useState('');
    const [showCustomModal, setShowCustomModal] = useState(false);
    const [accessCode, setAccessCode] = useState<null | string>(null);
    const { coordinates, isEnabled, askForPermissionIfNeeded, error: geoLocationError, isSupported } = useGeolocation();
    const [visitLoader, setVisitLoader] = useState(false);
    const [appointmentStatus, setAppointmentStatus] = useState(-1);
    const { id: vendorIdFromUrl } = useParams<{ id: string }>();

    useEffect(() => {
        if (!!doorOpenData && doorOpenData?.message) {
            setShowToast(true);
            setToastMessage(doorOpenData.message);
            setToastType('success');

            setTimeout(() => {
                setShowToast(false);
                dispatch(resetAmazonDoor());
            }, 4000);
        } else if (!!doorOpenError) {
            setShowToast(true);
            setToastMessage(doorOpenError);
            setToastType('error');

            setTimeout(() => {
                setShowToast(false);
                dispatch(resetAmazonDoor());
            }, 4000);
        }
    }, [doorOpenData, doorOpenError, dispatch]);

    useEffect(() => {
        if (vendorIdFromUrl) {
            dispatch(getVendorVisit(vendorIdFromUrl));
        }
    }, []);

    useEffect(() => {
        if (!vendorLoading && vendorData?.vendor) {
            const accessStatus = isAccessible(
                vendorData.vendor.startDate,
                vendorData.vendor.endDate,
                vendorData.vendor.startTime,
                vendorData.vendor.endTime,
            );
            setAppointmentStatus(accessStatus);
        }
    }, [vendorLoading, vendorData]);

    const getPlatform = (): 'android' | 'ios' | 'default' => {
        const userAgentData = (navigator as any).userAgentData;

        if (userAgentData?.platform) {
            const platform = userAgentData.platform.toLowerCase();
            if (platform.includes('android')) return 'android';
            if (platform.includes('iphone') || platform.includes('ipad') || platform.includes('ios')) return 'ios';
        }

        const userAgent = navigator.userAgent.toLowerCase();
        if (/android/.test(userAgent)) return 'android';
        if (/iphone|ipad|ipod/.test(userAgent)) return 'ios';

        if (
            navigator.platform === 'MacIntel' &&
            navigator.maxTouchPoints > 1 &&
            /safari/.test(userAgent) &&
            !/chrome|firefox|edg|opr/.test(userAgent) // Ensure it's not Ubuntu
        ) {
            return 'ios';
        }
        return 'default'; // If nothing matches
    };

    const getBrowserName = (): string => {
        const userAgent = navigator.userAgent.toLowerCase();
        const userAgentData = (navigator as any).userAgentData;

        // iOS Browsers
        if (userAgent.includes('crios')) return 'Chrome (iOS)';
        if (userAgent.includes('fxios')) return 'Firefox (iOS)';
        if (userAgent.includes('edgios')) return 'Edge (iOS)';

        // Chromium-based browsers
        if (userAgent.includes('duckduckgo')) return 'DuckDuckGo';
        if (userAgent.includes('vivaldi')) return 'Vivaldi';
        if (userAgent.includes('samsungbrowser')) return 'Samsung Internet';
        if (userAgent.includes('yabrowser')) return 'Yandex Browser';
        if (userAgent.includes('ucbrowser')) return 'UC Browser';

        // More reliable Brave detection
        const isBrave =
            (navigator as any).brave?.isBrave || // Check if Brave explicitly exposes itself
            userAgentData?.brands?.some((brand: { brand: string }) => brand.brand.toLowerCase() === 'brave'); // Check using userAgentData

        if (isBrave) return 'Brave';

        // Standard Browsers
        if (userAgent.includes('opr') || userAgent.includes('opera')) return 'Opera';
        if (userAgent.includes('chrome') && !userAgent.includes('edg') && !userAgent.includes('opr')) return 'Chrome';
        if (userAgent.includes('firefox')) return 'Firefox';
        if (userAgent.includes('safari') && !userAgent.includes('chrome')) return 'Safari';
        if (userAgent.includes('edg')) return 'Edge';
        if (userAgent.includes('trident') || userAgent.includes('msie')) return 'Internet Explorer';

        return 'Unknown';
    };

    const CustomModalBody = () => {
        // const platform: "android" | "ios" | "default" = getPlatform();
        const platform: 'android' | 'ios' | 'default' = getPlatform();
        const browser: string = getBrowserName();

        const instructions: Record<string, { message: string }> = {
            android: {
                message: `To enable location:\n<span>1️⃣ Open ${browser}.</span><span>2️⃣ Go to Settings > Site Settings.</span><span>3️⃣ Enable location for this website.</span>`,
            },
            ios: {
                message: `To enable location:\n<span>1️⃣ Open Settings.</span><span>2️⃣ Go to Privacy & Security > Location Services.</span><span>3️⃣ Enable location for ${browser}.</span>`,
            },
            default: {
                message: 'Please enable location permissions in your browser settings.',
            },
        };

        const { message } = instructions[platform] || instructions.default;

        return (
            <div className={styles.modalBodyContainer}>
                <div className={`${styles.modalHeader} allowSVG`}>
                    <h3 className={styles.modalH3}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M12 2C7.6 2 4 5.6 4 10C4 15.4 11 21.5 11.3 21.8C11.5 21.9 11.8 22 12 22C12.2 22 12.5 21.9 12.7 21.8C13 21.5 20 15.4 20 10C20 5.6 16.4 2 12 2ZM12 19.7C9.9 17.7 6 13.4 6 10C6 6.7 8.7 4 12 4C15.3 4 18 6.7 18 10C18 13.3 14.1 17.7 12 19.7ZM12 6C9.8 6 8 7.8 8 10C8 12.2 9.8 14 12 14C14.2 14 16 12.2 16 10C16 7.8 14.2 6 12 6ZM12 12C10.9 12 10 11.1 10 10C10 8.9 10.9 8 12 8C13.1 8 14 8.9 14 10C14 11.1 13.1 12 12 12Z"
                                fill="black"
                            />
                        </svg>
                        <span>Enable Location Access</span>
                    </h3>
                </div>
                <div className={styles.modalBodyInnerContainer}>
                    <p className={styles.preWrappedMessage} dangerouslySetInnerHTML={{ __html: message }}></p>
                </div>

                <div className={styles.modalFooter}>
                    <button className={styles.captureBtn} onClick={() => setShowCustomModal(false)}>
                        Okay
                    </button>
                </div>
            </div>
        );
    };

    const amazoneAccessDoor = async () => {
        if (isSupported && !isEnabled) {
            const locationAllowed = await askForPermissionIfNeeded();
            if (!locationAllowed) {
                openLocationSettings();
                return;
            }
        }
        if (isSupported && isEnabled && !geoLocationError && coordinates?.latitude && coordinates?.longitude) {
            const appointmentObj: amazonDoorProps = {
                locationId: vendorData.vendor?.locationId,
                vendorId: vendorData.vendor.vendorId,
                latitude: coordinates.latitude,
                longitude: coordinates.longitude,
                timestamp: Math.floor(Date.now()),
                amazonFlag: AmazoneFlag.Vendor,
            };
            onOpenAmazonDoor(appointmentObj);
        } else {
            setShowToast(true);
            setToastMessage('Please enable location permissions to unlock the door.');
            setToastType('error');
            setTimeout(() => {
                setShowToast(false);
            }, 3000);
        }
    };

    const onOpenAmazonDoor = (appointmentObj: amazonDoorProps) => {
        dispatch(openAmazonDoor(appointmentObj));
    };

    const onKioskSuccess = async (accessCode: string) => {
        setAccessCode(accessCode);
    };

    const onBMSSuccess = (message: string) => {
        setToastType('success');
        setShowToast(true);
        setToastMessage(message);
    };

    const onFailure = (message: string) => {
        setShowToast(true);
        setToastType('error');
        setToastMessage(message);
    };

    function openLocationSettings() {
        setShowCustomModal(true);
    }

    const getAppointmentAccessCode = async () => {
        if (isSupported && isEnabled && coordinates?.latitude && coordinates?.longitude) {
            const appointmentAccessCodeData: vendorAppointmentAccessCodeProps = {
                vendorId: vendorData?.vendor?.vendorId,
                latitude: coordinates.latitude.toString(),
                longitude: coordinates.longitude.toString(),
            };
            dispatch(getVendorLocationVisit(appointmentAccessCodeData, onKioskSuccess, onBMSSuccess, onFailure, setVisitLoader));
        }
        if (!isEnabled && geoLocationError) {
            openLocationSettings();
        }
        if (isSupported && !isEnabled) {
            const isAllowed = await askForPermissionIfNeeded();
            if (coordinates?.latitude && coordinates?.longitude && isAllowed) {
                const appointmentAccessCodeData: vendorAppointmentAccessCodeProps = {
                    vendorId: vendorData?.vendor?.vendorId,
                    latitude: coordinates.latitude.toString(),
                    longitude: coordinates.longitude.toString(),
                };
                dispatch(getVendorLocationVisit(appointmentAccessCodeData, onKioskSuccess, onBMSSuccess, onFailure, setVisitLoader));
            }
            if (!isAllowed) {
                openLocationSettings();
            }
        }
    };

    return (
        <div className={styles.container}>
            {(visitLoader || vendorLoading || doorOpenloading) && <Loader />}
            {!vendorLoading && vendorData && vendorData.vendor && appointmentStatus !== -1 ? (
                <>
                    <div className={styles.upperContainer}>
                        <div className={styles.appointmentTitle}>
                            <h1>Hello, {vendorData?.vendor?.vendorName}</h1>
                        </div>

                        <div className={styles.inlineItems}>
                            <div className={styles.detailWrapper}>
                                <p className={styles.title}>Location Name</p>
                                <p className={styles.info}>{vendorData?.vendor?.location}</p>
                            </div>
                        </div>

                        <div className={styles.inlineItems}>
                            <div className={styles.detailWrapper}>
                                <p className={styles.title}>Address</p>
                                <p className={styles.info}>
                                    {vendorData?.vendor?.street1}, {vendorData?.vendor?.city}, {vendorData?.vendor?.state},{' '}
                                    {vendorData?.vendor?.zip}
                                </p>
                            </div>
                        </div>

                        <div className={styles.inlineItems}>
                            <div className={styles.detailWrapper}>
                                <p className={styles.title}>Start Date</p>
                                <p className={styles.info}>
                                    {vendorData?.vendor?.startDate ? moment(vendorData.vendor.startDate).format('MM-DD-YYYY') : '-'}
                                </p>
                            </div>
                            <div className={styles.detailWrapper}>
                                <p className={styles.title}>Start Time</p>
                                <p className={styles.info}>
                                    {vendorData?.vendor?.startTime ? moment(vendorData.vendor.startTime, 'HH:mm A').format('hh:mm A') : '-'}
                                </p>
                            </div>
                        </div>

                        <div className={styles.inlineItems}>
                            <div className={styles.detailWrapper}>
                                <p className={styles.title}>End Date</p>
                                <p className={styles.info}>
                                    {vendorData?.vendor?.endDate ? moment(vendorData.vendor.endDate).format('MM-DD-YYYY') : '-'}
                                </p>
                            </div>
                            <div className={styles.detailWrapper}>
                                <p className={styles.title}>End Time</p>
                                <p className={styles.info}>
                                    {vendorData?.vendor?.endTime ? moment(vendorData.vendor.endTime, 'HH:mm A').format('hh:mm A') : '-'}
                                </p>
                            </div>
                        </div>

                        {vendorData?.vendor?.locker !== null && (
                            <div className={styles.inlineItems}>
                                <div className={styles.detailWrapper}>
                                    <p className={styles.title}>Locker Number</p>
                                    <p className={styles.info}>{vendorData?.vendor?.locker}</p>
                                </div>
                            </div>
                        )}

                        {vendorData?.vendor?.vendorSpecialNote === '' ? (
                            ''
                        ) : (
                            <div className={styles.inlineItems}>
                                <div className={styles.detailWrapper}>
                                    <p className={styles.title}>Special Notes</p>
                                    <p className={styles.info}>{vendorData?.vendor?.vendorSpecialNote}</p>
                                </div>
                            </div>
                        )}
                        {vendorData?.vendor.amazonKeyAccessId && appointmentStatus === 1 && (
                            <div className={styles.amazoneBtn}>
                                <SwiperButton loading={vendorLoading} onDragend={() => amazoneAccessDoor()} />
                            </div>
                        )}

                        {
                            <div className={styles.lowerBtn}>
                                <div className={styles.infoBox}>
                                    <div className={styles.leftContainer}>
                                        <LockIcon />
                                    </div>
                                    <div className={styles.rightContainer}>
                                        <p>{vendorData?.vendor?.lockerName?.toLowerCase() === 'kiosk' ? 'Kiosk Code' : 'BMS Locker'}</p>
                                        <p
                                            className={`${appointmentStatus !== 1 ? styles.noStyle : ''}`}
                                            onClick={() => {
                                                if (appointmentStatus === 1) {
                                                    getAppointmentAccessCode();
                                                }
                                            }}>
                                            {appointmentStatus === 1
                                                ? vendorData?.vendor?.lockerName?.toLowerCase() === 'kiosk'
                                                    ? accessCode || 'Tap here to access code'
                                                    : 'Tap here to open locker'
                                                : vendorData?.vendor?.lockerName?.toLowerCase() === 'kiosk'
                                                ? 'Tap here to access code'
                                                : 'Tap here to open locker'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        }

                        {vendorData?.vendor?.leasingAgentNumber && (
                            <div className={styles.leasingContactBox}>
                                <p className={styles.leasingText}>Have questions? Call us:</p>
                                <div className={styles.leasingNumberBox}>
                                    <a href={`tel:+${vendorData?.vendor?.leasingAgentNumber}`} className={styles.leasingLink}>
                                        <LeaasingNumberIcon />
                                        <span>
                                            {vendorData?.vendor?.leasingAgentNumber
                                                ? formatPhoneNumberIntl(`+${vendorData.vendor.leasingAgentNumber}`)
                                                : ''}
                                        </span>
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                </>
            ) : (
                !vendorLoading &&
                (!vendorData || !vendorData.vendor || appointmentStatus === -1) && (
                    <div className={styles.noPropertyWrapper}>
                        <img src={SorryPineapple} alt="no property" width={132} height={212} />
                        <p>Appointment no longer active</p>
                    </div>
                )
            )}

            {showCustomModal && (
                <Popup setOpen={setShowCustomModal} className="custom-webcam-modal" open={showCustomModal} bodyData={<CustomModalBody />} />
            )}
            {showToast && <Toast message={toastMessage} type={toastType || 'error'} />}
        </div>
    );
};

export default VendorAppointments;
